/**
 * Іконки статус-колонок списків документів (рішення власника 2026-08-23):
 * «Проведено» / «Вкладення» / «Підпис» — без назв, лише сучасні SVG-іконки;
 * порядок у КОЖНІЙ таблиці: бейдж міграції → проведено → вкладення → підпис →
 * решта колонок (нормалізація у docStatusCols.ts, застосовується автоматично
 * в InfiniteDocGrid і DataTable).
 *
 * Проведено: галочка у колі — зелена для проведеного, ЧЕРВОНА для не
 * проведеного (як у 1С, але з кольоровим акцентом на чернетці).
 */
import { isDraft1cNote } from '@/shared/utils/unpostedNotes';
import type { SignStatus } from '@/api/signControl';
import { SIGN_STATUS_LABELS } from '@/api/signControl';

const GREEN = '#15803d';
const RED = '#dc2626';
const BLUE = '#2563eb';
const GRAY = '#94a3b8';

const base = { width: 14, height: 14, display: 'inline-block', verticalAlign: 'middle' } as const;

/** Галочка у колі (проведено / не проведено). `note` (ч.1218) — документ перенесено з 1С
 *  і не проведено ЗА ЗАДУМОМ: сіре коло з рискою і поясненням замість червоної відмітки. */
export function PostedIcon({ posted, size = 14, note }: { posted: boolean; size?: number; note?: string | null }) {
  if (!posted && note) {
    // ч.1247: чернетку 1С (у 1С не проведено / позначено на видалення) бухгалтер провести
    // може — тому без «Не проводиться»; пояснення сервера починається з «У 1С цей документ».
    const draft1c = isDraft1cNote(note);
    return (
      <svg viewBox="0 0 24 24" style={{ ...base, width: size, height: size }}
        aria-label={draft1c ? 'Чернетка з 1С' : 'Не проводиться'} data-unposted-by-design="">
        <title>{draft1c ? note : `Не проводиться. ${note}`}</title>
        <circle cx="12" cy="12" r="10" fill="#f1f5f9" stroke={GRAY} strokeWidth="1.6" />
        <path d="M7.5 12h9" fill="none" stroke={GRAY} strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    );
  }
  const color = posted ? GREEN : RED;
  return (
    <svg viewBox="0 0 24 24" style={{ ...base, width: size, height: size }}
      aria-label={posted ? 'Проведено' : 'Не проведено'}>
      <title>{posted ? 'Проведено' : 'Не проведено'}</title>
      <circle cx="12" cy="12" r="10" fill={posted ? '#dcfce7' : '#fee2e2'} stroke={color} strokeWidth="1.6" />
      <path d="M7.5 12.5l3 3 6-6.5" fill="none" stroke={color} strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Скріпка — є вкладення. */
export function AttachmentIcon({ has, size = 14 }: { has: boolean; size?: number }) {
  if (!has) return null;
  return (
    <svg viewBox="0 0 24 24" style={{ ...base, width: size, height: size }} aria-label="Є вкладення">
      <title>Є вкладення</title>
      <path d="M21 11.5l-8.5 8.5a5.5 5.5 0 0 1-7.8-7.8l9-9a3.5 3.5 0 0 1 5 5l-9 9a1.5 1.5 0 0 1-2.1-2.1l8.3-8.3"
        fill="none" stroke={BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Підпис: перо — зелене (є підписаний документ) / червоне (очікує). */
export function SignIcon({ status, size = 14 }: { status?: SignStatus | null; size?: number }) {
  if (!status) return null;
  const unsigned = status === 'UNSIGNED';
  const color = unsigned ? RED : GREEN;
  const label = unsigned ? 'Немає підписаного документа (ЕЦП або скан)' : SIGN_STATUS_LABELS[status];
  return (
    <svg viewBox="0 0 24 24" style={{ ...base, width: size, height: size }} aria-label={label}>
      <title>{label}</title>
      <path d="M4 20h16" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M14.5 4.5l5 5L9 20H4v-5L14.5 4.5z" fill={unsigned ? '#fee2e2' : '#dcfce7'}
        stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
      {unsigned && <path d="M3 3l18 18" stroke={color} strokeWidth="1.6" strokeLinecap="round" />}
    </svg>
  );
}

/** Сірі іконки для ЗАГОЛОВКІВ статус-колонок. */
export function PostedHeaderIcon() {
  return (
    <svg viewBox="0 0 24 24" style={base} aria-label="Проведено"><title>Проведено</title>
      <circle cx="12" cy="12" r="10" fill="none" stroke={GRAY} strokeWidth="1.6" />
      <path d="M7.5 12.5l3 3 6-6.5" fill="none" stroke={GRAY} strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function AttachmentHeaderIcon() {
  return (
    <svg viewBox="0 0 24 24" style={base} aria-label="Вкладення"><title>Вкладення</title>
      <path d="M21 11.5l-8.5 8.5a5.5 5.5 0 0 1-7.8-7.8l9-9a3.5 3.5 0 0 1 5 5l-9 9a1.5 1.5 0 0 1-2.1-2.1l8.3-8.3"
        fill="none" stroke={GRAY} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function SignHeaderIcon() {
  return (
    <svg viewBox="0 0 24 24" style={base} aria-label="Підпис"><title>Підпис</title>
      <path d="M4 20h16" stroke={GRAY} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M14.5 4.5l5 5L9 20H4v-5L14.5 4.5z" fill="none" stroke={GRAY} strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}
